// SettingInvoicePage.js
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import InputFieldAddress from '../components/InputFieldAddress';
import TableView from '../components/TableView';
import DefinitionRow from '../components/DefinitionRow';
import BankAccountDialog from '../components/BankAccountDialog';
import { Typography, Button, Stack, TextField, Paper, IconButton, Alert } from '@mui/material';
import { Sync, Apartment, AccountBalance, Edit, Close, Delete, Add, Upload } from '@mui/icons-material';

const SettingInvoicePage = ({ hasLimit }) => {
  const { showSnackbar } = useSnackbar();
  const [isEditMode, setIsEditMode] = useState(false);
  const [error, setError] = useState(null);
  const [logoImageFile, setLogoImageFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  /** 口座 */
  const [bankAccounts, setBankAccounts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const { register, handleSubmit, setValue, reset, watch, formState: { errors }, control, } = useForm();
  const logoImageUrl = watch('logo_image_url');
  const fetchData = async () => {
    try {
      const res = await axiosInstance.get('/setting/invoice');
      const data = res.data || {};
      reset(data);
      setBankAccounts(data.bank_accounts || []);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      setError(err?.response?.data?.message || '取得に失敗しました。');
    }
  };
  useEffect(() => {
    fetchData();
  }, [reset]);

  /** 請求情報のみ保存 */
  const onSubmit = async (data) => {
    try {
      const formData = new FormData();

      // ロゴ削除
      if (removeLogo) {
        formData.append('remove_logo', '1');
      }

      // ロゴ画像
      if (logoImageFile) {
        formData.append('logo_image', logoImageFile);
      }

      // テキスト項目
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value ?? '');
      });

      const res = await axiosInstance.post('/setting/invoice', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      showSnackbar(res?.data?.message || '請求情報を保存しました', 'success');

      await fetchData();

      setLogoImageFile(null);
      setRemoveLogo(false);
      setIsEditMode(false);

    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '保存に失敗しました', 'error');
    }
  };

  /** 口座追加 */
  const openAdd = () => {
    setEditIndex(null);
    setOpenDialog(true);
  };

  /** 口座編集 */
  const openEdit = (index) => {
    setEditIndex(index);
    setOpenDialog(true);
  };

  /** 口座削除 */
  const handleDelete = async (index) => {
    if (!window.confirm('削除します。よろしいですか？')) return;

    try {
      const account = bankAccounts[index];
      if (account?.invoice_id) {
        await axiosInstance.delete(`/setting/invoice/bank_account/${account.invoice_id}`);
      }
      fetchData();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '削除に失敗しました', 'error');
    }
  };
  const logoPreviewUrl = React.useMemo(() => {
    if (!logoImageFile) return null;
    return URL.createObjectURL(logoImageFile);
  }, [logoImageFile]);
  const currentLogoUrl =
    removeLogo
      ? null
      : logoPreviewUrl ||
      (logoImageUrl
        ? `${process.env.REACT_APP_IMAGE_BASE_URL}${logoImageUrl}`
        : null);
  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Typography variant="h1" mb={2}>会社設定</Typography>

      {/* 請求情報フォーム */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper variant="section">
          <Stack direction="row" spacing={1} alignItems="center">
            <Apartment color="primary" />
            <Typography variant="h4">自社情報</Typography>
          </Stack>
          <Alert severity="info" sx={{ my: 1 }}>
            自社情報は各種帳票に記載されます。
          </Alert>
          <DefinitionRow label="会社名*">
            <TextField
              {...register('company_name', {
                required: '会社名は必須です',
                maxLength: {
                  value: 100,
                  message: '会社名は100文字以内で入力してください',
                },
              })}
              type="text"
              placeholder="サンプル株式会社"
              disabled={!isEditMode}
              inputProps={{ maxLength: 100 }}
              error={!!errors?.company_name}
              helperText={errors?.company_name?.message}
            />
          </DefinitionRow>
          <DefinitionRow label="所在地 *">
            <InputFieldAddress
              register={register}
              errors={errors}
              isEditMode={isEditMode}
              setValue={setValue}
              control={control}
            />
          </DefinitionRow>
          <DefinitionRow label="電話番号">
            <TextField
              {...register('company_phone_number', {
                pattern: {
                  value: /^(?:\+?\d{1,4}[-\s]?)?0?\d{1,4}[-\s]?\d{1,4}[-\s]?\d{4}$/,
                  message: '電話番号の形式が正しくありません',
                },
                maxLength: {
                  value: 20,
                  message: '電話番号は20文字以内で入力してください',
                },
              })}
              type="tel"
              placeholder="03-1234-5678"
              disabled={!isEditMode}
              inputProps={{ maxLength: 20 }}
              error={!!errors?.company_phone_number}
              helperText={errors?.company_phone_number?.message}
            />
          </DefinitionRow>

          <DefinitionRow label="メールアドレス">
            <TextField
              {...register('company_email', {
                pattern: {
                  value: /^[\w.+-]+@[\w.-]+\.[a-zA-Z]{2,}$/,
                  message: 'メールアドレスの形式が正しくありません',
                },
                maxLength: {
                  value: 255,
                  message: 'メールアドレスは255文字以内で入力してください',
                },
              })}
              type="email"
              placeholder="info@example.com"
              disabled={!isEditMode}
              inputProps={{ maxLength: 255 }}
              error={!!errors?.company_email}
              helperText={errors?.company_email?.message}
            />
          </DefinitionRow>
          <DefinitionRow label="請求担当者名">
            <TextField
              {...register('invoice_contact_name', {
                maxLength: {
                  value: 100,
                  message: '担当者名は100文字以内で入力してください',
                },
              })}
              type="text"
              placeholder="例：経理担当 田中太郎"
              disabled={!isEditMode}
              inputProps={{ maxLength: 100 }}
              error={!!errors?.invoice_contact_name}
              helperText={errors?.invoice_contact_name?.message}
            />
          </DefinitionRow>

          <DefinitionRow label="適格請求書発行事業者番号（インボイス番号）">
            <TextField
              {...register('invoice_number', {
                maxLength: {
                  value: 20,
                  message: 'インボイス番号は20文字以内で入力してください',
                },
              })}
              type="text"
              placeholder="T1234567890123"
              disabled={!isEditMode}
              inputProps={{ maxLength: 20 }}
              error={!!errors?.invoice_number}
              helperText={errors?.invoice_number?.message}
            />
          </DefinitionRow>

          <DefinitionRow label="インボイス登録年月日">
            <TextField
              {...register('invoice_registered_date')}
              type="date"
              disabled={!isEditMode}
              error={!!errors?.invoice_registered_date}
              helperText={errors?.invoice_registered_date?.message}
            />
          </DefinitionRow>

          <DefinitionRow label="請求書備考">
            <TextField
              {...register('invoice_note', {
                maxLength: {
                  value: 2000,
                  message: '請求書備考は2000文字以内で入力してください',
                },
              })}
              type="text"
              placeholder="例：お支払いは翌月末までにお願いします"
              disabled={!isEditMode}
              multiline
              minRows={4}
              inputProps={{ maxLength: 2000 }}
              error={!!errors?.invoice_note}
              helperText={errors?.invoice_note?.message}
            />
          </DefinitionRow>
          <DefinitionRow label="帳票用会社ロゴ画像">
            <Stack alignItems="start" spacing={1}>
              {currentLogoUrl && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={currentLogoUrl}
                    alt="ロゴ画像プレビュー"
                    style={{ maxHeight: 80, maxWidth: '100%' }}
                  />
                  {isEditMode && (
                    <IconButton
                      onClick={() => {
                        setLogoImageFile(null);
                        setValue('logo_image_url', null);
                        setRemoveLogo(true);
                      }}
                      style={{ position: 'absolute', top: 0, right: 0 }}
                    >
                      <Close />
                    </IconButton>
                  )}
                </div>
              )}

              <Button
                variant="outlined"
                component="label"
                startIcon={<Upload />}
                disabled={!isEditMode}
              >
                画像を選択
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setLogoImageFile(file || null);
                    setRemoveLogo(false);
                  }}
                />
              </Button>
            </Stack>
          </DefinitionRow>
          {/* 操作ボタン */}
          <Stack direction="row" spacing={1} mt={2}>
            <Button
              variant="outlined"
              startIcon={isEditMode ? <Close /> : <Edit />}
              onClick={() => setIsEditMode(v => !v)}
            >
              {isEditMode ? '編集を破棄する' : '編集する'}
            </Button>

            {isEditMode && (
              <Button type="submit" variant="contained" endIcon={<Sync />}>
                保存
              </Button>
            )}
          </Stack>
        </Paper>
      </form>

      {/* 口座情報セクション */}
      <Paper variant="section">
        <Stack direction="row" spacing={1} alignItems="center">
          <AccountBalance color="primary" />
          <Typography variant="h4">口座情報</Typography>
        </Stack>
        <Alert severity="info" sx={{ my: 1 }}>
          口座情報は請求書に記載されます。
        </Alert>
        <TableView
          dataset={bankAccounts}
          keyColumn="invoice_id"
          columns={[
            {
              label: '銀行名', accessor: 'bank_name', width: 150,
              render: (row) => (
                <Typography>
                  {row.bank_name}
                </Typography>
              ),
            },
            {
              label: '支店名', accessor: 'bank_branch_name', width: 120,
              render: (row) => (
                <Typography>
                  {row.bank_branch_name}
                </Typography>
              ),
            },
            {
              label: '種別', accessor: 'bank_account_type', width: 80,
              render: (row) => (
                <Typography>
                  {row.bank_account_type}
                </Typography>
              ),
            },
            {
              label: '口座番号', accessor: 'bank_account_number', width: 120,
              render: (row) => (
                <Typography>
                  {row.bank_account_number}
                </Typography>
              ),
            },
            {
              label: '口座名義', accessor: 'bank_account_holder', width: 180,
              render: (row) => (
                <Typography>
                  {row.bank_account_holder}
                </Typography>
              ),
            },
            {
              label: '',
              width: 60,
              render: (_, index) =>
                <>
                  <IconButton size="small" onClick={() => openEdit(index)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(index)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </>
            }
          ]}
        />
        <Button variant='outlined' startIcon={<Add />} size="small" onClick={openAdd} sx={{ mt: 1 }}>
          追加
        </Button>
      </Paper>

      {/* 口座情報ダイアログ */}
      {openDialog && (
        <BankAccountDialog
          open={openDialog}
          account={editIndex !== null ? bankAccounts[editIndex] : null}
          onClose={() => setOpenDialog(false)}
          onSaved={() => fetchData()}
        />
      )}
    </>
  );
};

export default SettingInvoicePage;
